import React, {useContext, useEffect, useRef, useState} from 'react';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Logout from '@mui/icons-material/Logout';
import BeachAccess from '@mui/icons-material/BeachAccess';
import {AccountCircle} from "@mui/icons-material";
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import {useAuth} from "../../../hooks/useAuth";
import {useNavigate} from "react-router-dom";
import {styled} from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import {AppBarProps as MuiAppBarProps} from "@mui/material/AppBar";
import {routeConfigs} from "../../../navigation/routeConfig";
import {IUser} from "../../../queries/user/userModel";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import {Badge, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, Divider, Drawer, Menu, MenuItem, ListItemIcon, ListItemText} from "@mui/material";
import {contextObject} from "../../shared/ShoppingCart/ShoppingCartContext";
import ShoppingCart from "../../shared/ShoppingCart/ShoppingCart";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";

import logoSrc from '../AuthLayout/brandLogo.svg';
import { useMutationDeleteAllCart } from '../../../queries/product/hooks/useMutationDeleteAllCart';
import { CART } from '../../../queries/product/productTypeDefs';
import { useQueryUserSavings } from '../../../queries/user/hooks/useQueryUserSavings';
import { currencyFormat } from '../../utils/utils';

const drawerWidth: number = 220;
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  boxShadow: 'none',
  borderBottom: 'none',
  ...(open && {
    // Drawer uses content-box + 1px borderRight, so total occupied width is drawerWidth + 1.
    marginLeft: drawerWidth + 1,
    width: `calc(100% - ${drawerWidth + 1}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const StyledBadge = styled(Badge)({
  "& .MuiBadge-badge": {
    color: "#FFFFFF",
    backgroundColor: "#F97316",
    fontWeight: "bold",
  },
});

interface IHeaderProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: IUser;
  pharmacy: any;
  isAdmin: boolean;
}
export const Header:React.FunctionComponent<IHeaderProps> = ({
  open,
  setOpen,
  user,
  pharmacy,
  isAdmin
}) => {
  const { signOut } = useAuth();
  const navigate  = useNavigate();
  const [cartOpen, setCartOpen] = useState<boolean>(false);
  const {cart, loading} = useContext(contextObject);
  const [openCartNotification, setOpenCartNotification] = useState<boolean>(false);
  const timer = useRef<any>(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const accountMenuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accountMenuOpen = Boolean(accountMenuAnchor);

  const openAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (accountMenuCloseTimer.current) {
      clearTimeout(accountMenuCloseTimer.current);
      accountMenuCloseTimer.current = null;
    }
    setAccountMenuAnchor(event.currentTarget);
  };

  const scheduleCloseAccountMenu = () => {
    if (accountMenuCloseTimer.current) {
      clearTimeout(accountMenuCloseTimer.current);
    }
    accountMenuCloseTimer.current = setTimeout(() => {
      setAccountMenuAnchor(null);
    }, 150);
  };

  const keepAccountMenuOpen = () => {
    if (accountMenuCloseTimer.current) {
      clearTimeout(accountMenuCloseTimer.current);
      accountMenuCloseTimer.current = null;
    }
  };
  const [deleteAllCartItems, { deleteLoading }] = useMutationDeleteAllCart({
    refetchQueries: [
      {
        query: CART,
        variables: {
          where: { userId: { equals: +user.id } },
          orderBy: { createdAt: 'asc' }
        },
        awaitRefetchQueries: true,
      },
    ],
  });
  
  const accountName = isAdmin ? `${user.firstName} ${user.lastName}` : pharmacy.legalName;
  const { data: savingsData, loading: savingsLoading } = useQueryUserSavings({
    fetchPolicy: 'cache-and-network',
    skip: isAdmin,
  });
  const savingsAmount: number = savingsData?.savingsWithPrimePostRx ?? 0;
  const vacationIsActive = Boolean(user?.vacationIsActive);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const toggleDrawerCart = (anchor: any, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setCartOpen(!cartOpen);
  };

  useEffect(() => {
    if ( timer.current ) {
      clearTimeout(timer.current);
    }
    if (cart.length > 0) {
      timer.current = setTimeout(() => setOpenCartNotification(true), 3600000);
    }
    return () => {
      timer.current && clearTimeout(timer.current);
    };
  }, [cart])

  const refreshTimeout = () => {
    setOpenCartNotification(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpenCartNotification(true), 10 * 1000);
  };

  const emptyCart = () => {
    deleteAllCartItems({variables: { where: { userId: {equals: +user.id} } }}).then(() => {
      setOpenCartNotification(false);
    });
  }

  return (
    <AppBar position="absolute" open={open} elevation={0} sx={{ bgcolor: '#FFFFFF' }}>
      <Toolbar
        sx={{
          pr: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: '#FFFFFF',
          color: '#1E293B',
          height: 64,
          minHeight: '64px !important',
          boxSizing: 'border-box',
          borderBottom: '1px solid #E2E8F0',
          fontFamily: '"Source Sans 3", sans-serif',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{ marginRight: '36px', ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Link href={routeConfigs.buy.path} sx={{ display: 'flex', alignItems: 'center' }} variant="body2" >
            <img height={44} src={logoSrc} alt='PrimePostRx' />
          </Link>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'end'}}>
          {!isAdmin && (
            <>
              <StyledBadge
                badgeContent={cart.length}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ ml: 1 }}
              >
                {(loading || deleteLoading) ? (
                  <CircularProgress color="inherit" />
                ) : (
                  <ShoppingCartIcon
                    sx={{
                      cursor: 'pointer',
                      fontSize: 28,
                      transition: 'transform 0.25s ease',
                      '&:hover': {
                        transform: 'scale(1.15) rotate(-8deg)',
                      },
                    }}
                    onClick={cart.length > 0 ? toggleDrawerCart('right', true) : undefined}
                  />
                )}
              </StyledBadge>
              <Drawer
                anchor='right'
                open={cartOpen}
                onClose={toggleDrawerCart('right', false)}
                sx={{
                  // AppBar uses drawer + 1; keep cart/backdrop above so the header isn't left undimmed.
                  zIndex: (theme) => theme.zIndex.drawer + 2,
                }}
                PaperProps={{
                  sx: {
                    width: "100%",
                    maxWidth: 480,
                    bgcolor: "#FFFFFF",
                    borderLeft: "1px solid #E2E8F0",
                    boxShadow: "0 8px 28px rgba(15, 23, 42, 0.12)",
                  },
                }}
              >
                <ShoppingCart setCartOpen={setCartOpen} />
              </Drawer>
              {openCartNotification && (
                <Dialog
                  fullWidth
                  maxWidth='sm'
                  open={Boolean(openCartNotification)}
                  onClose={refreshTimeout}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                >
                  <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                      Are you still interested in purchasing this item?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      variant='outlined'
                      onClick={emptyCart}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      onClick={refreshTimeout}
                    >
                      Buy
                    </Button>
                  </DialogActions>
                </Dialog>
              )}
            </>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 1,
              ml: 3,
              cursor: 'pointer',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              '&:hover': { bgcolor: '#F1F5F9' },
            }}
            onMouseEnter={openAccountMenu}
            onMouseLeave={scheduleCloseAccountMenu}
          >
            <AccountCircle sx={{ fontSize: 28 }} />
            <Box sx={{ ml: 1, fontSize: 16.8, fontWeight: 500 }}>{accountName}</Box>
            <Menu
              anchorEl={accountMenuAnchor}
              open={accountMenuOpen}
              onClose={() => setAccountMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{
                onMouseEnter: keepAccountMenuOpen,
                onMouseLeave: scheduleCloseAccountMenu,
                sx: { py: 0.5, minWidth: 240 },
              }}
              slotProps={{
                paper: {
                  onMouseEnter: keepAccountMenuOpen,
                  onMouseLeave: scheduleCloseAccountMenu,
                  sx: { mt: 0.5 },
                },
              }}
              disableScrollLock
            >
              {!isAdmin && [
                <MenuItem
                  key="pharmacy-profile"
                  onClick={() => {
                    setAccountMenuAnchor(null);
                    navigate(routeConfigs.pharmacyProfile.path);
                  }}
                >
                  <ListItemIcon>
                    <BusinessIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Pharmacy Profile" />
                </MenuItem>,
                <MenuItem
                  key="bank-info"
                  onClick={() => {
                    setAccountMenuAnchor(null);
                    navigate(routeConfigs.bankAccount.path);
                  }}
                >
                  <ListItemIcon>
                    <AccountBalanceIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Bank Info" />
                </MenuItem>,
                <MenuItem
                  key="manage-users"
                  onClick={() => {
                    setAccountMenuAnchor(null);
                    navigate(routeConfigs.users.path);
                  }}
                >
                  <ListItemIcon>
                    <GroupAddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Manage Users" />
                </MenuItem>,
                <MenuItem
                  key="compliance"
                  onClick={() => {
                    setAccountMenuAnchor(null);
                    navigate(routeConfigs.settingsCompliance.path);
                  }}
                >
                  <ListItemIcon>
                    <VerifiedUserIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Compliance & Verification" />
                </MenuItem>,
                <MenuItem
                  key="my-profile"
                  onClick={() => {
                    setAccountMenuAnchor(null);
                    navigate(routeConfigs.profile.path);
                  }}
                >
                  <ListItemIcon>
                    <PersonOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="My Profile" />
                </MenuItem>,
                <Divider key="account-menu-divider-1" sx={{ my: 0.5 }} />,
                <MenuItem
                  key="saved"
                  onClick={() => {
                    setAccountMenuAnchor(null);
                    navigate(routeConfigs.buyHistory.path);
                  }}
                >
                  <ListItemIcon>
                    <AccountBalanceWalletIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Saved"
                    secondary={savingsLoading ? '…' : currencyFormat(savingsAmount)}
                    secondaryTypographyProps={{
                      sx: {
                        color: '#2563EB',
                        fontWeight: 700,
                        fontSize: 12,
                      },
                    }}
                  />
                </MenuItem>,
                <MenuItem
                  key="vacation-mode"
                  onClick={() => {
                    setAccountMenuAnchor(null);
                    navigate(routeConfigs.vacationMode.path);
                  }}
                >
                  <ListItemIcon>
                    <BeachAccess fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Vacation Mode"
                    secondary={vacationIsActive ? 'Active' : 'Inactive'}
                    secondaryTypographyProps={{
                      sx: {
                        color: vacationIsActive ? '#C2410C' : '#2E7D32',
                        fontWeight: 600,
                        fontSize: 12,
                      },
                    }}
                  />
                </MenuItem>,
                <Divider key="account-menu-divider-2" sx={{ my: 0.5 }} />,
              ]}
              <MenuItem
                onClick={async () => {
                  setAccountMenuAnchor(null);
                  await signOut();
                  navigate('/login');
                }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default Header;